import { DebugClearStorageButton } from "@/components/shared/DebugClearStorageButton";
import { FadeSlide } from '@/components/motion/fade-slide';
import { STAGE_THEME_COLORS } from '@/theme/stage-colors';
import type { GameInstructionsScreenProps } from '@/types/game-instructions-screen';
import type {
  InstructionIconKey,
  InstructionSlide,
  StageInstructionContent,
  StageInstructionContentMap,
} from '@/types/instruction-content';
import type { StageId } from '@/types/stage-theme';
import { getCloudFrontAssetUrl } from '@/utils/asset-utils';
import type { i18n as I18nInstance } from 'i18next';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { INSTRUCTION_SCREEN_CONTENT_MAP } from './instruction-screen-content-map';
import './instructions-screen.css';
import { INSTRUCTION_ICONS } from './instruction-icons';

const STAGE_OVERLAYS: Record<StageId, string> = {
  1: getCloudFrontAssetUrl('stage-1/Stage_1.png'),
  2: getCloudFrontAssetUrl('stage-2/Stage_2.png'),
  3: getCloudFrontAssetUrl('stage-3/Stage_3.png'),
  4: getCloudFrontAssetUrl('stage-4/Stage_4.png'),
  5: getCloudFrontAssetUrl('stage-5/Stage_5.png'),
  6: getCloudFrontAssetUrl('stage-6/Stage_6.png'),
  7: getCloudFrontAssetUrl('stage-7/Stage_7.png'),
};

/**
 * Per-move presentation defaults, applied by position.
 *
 * Slides usually arrive from `game-metadata.json`, which carries copy only — no
 * icon, accent, or tag. These fill that gap so every game gets the styled moves
 * without touching its metadata; any slide may still override each field.
 * `tagLabelKey` is an i18n key rather than literal text so the chip translates
 * along with the rest of the screen.
 */
const MOVE_PRESETS: ReadonlyArray<{
  accent: string;
  icon: InstructionIconKey;
  tagLabelKey: string;
}> = [
  { accent: '#18B16C', icon: 'tickCircle', tagLabelKey: 'instructions.tags.basics' },
  { accent: '#F8C39B', icon: 'winner', tagLabelKey: 'instructions.tags.scoring' },
  { accent: '#CCE4FF', icon: 'danger', tagLabelKey: 'instructions.tags.antiCheat' },
];

const FALLBACK_PRESET = {
  accent: 'rgba(255, 255, 255, 0.45)',
  icon: 'closeCircle' as InstructionIconKey,
  tagLabelKey: '',
};

const getMovePreset = (index: number) => MOVE_PRESETS[index] ?? FALLBACK_PRESET;

const resolveGameLabel = (labelTemplate: string, stage: StageId): string =>
  labelTemplate.includes('{stage}')
    ? labelTemplate.replace('{stage}', String(stage))
    : labelTemplate;

function parseInstructionStage(
  i18n: I18nInstance,
  stage: StageId,
  override?: Partial<StageInstructionContentMap>,
): StageInstructionContent {
  const raw = i18n.t(`instructions.stages.${String(stage)}`, {
    returnObjects: true,
  });
  const i18nContent =
    typeof raw === 'string'
      ? INSTRUCTION_SCREEN_CONTENT_MAP[stage]
      : (raw as StageInstructionContent);

  const overrideContent = override?.[stage];
  if (!overrideContent) {
    return {
      ...i18nContent,
      gameLabel: resolveGameLabel(i18nContent.gameLabel, stage),
    };
  }

  return {
    ...i18nContent,
    ...overrideContent,
    slides: overrideContent.slides ?? i18nContent.slides,
    gameLabel: resolveGameLabel(overrideContent.gameLabel ?? i18nContent.gameLabel, stage),
    statusLabel: overrideContent.statusLabel ?? i18nContent.statusLabel,
    gameTitle: overrideContent.gameTitle ?? i18nContent.gameTitle,
  };
}

// ─── InstructionMove ────────────────────────────────────────────────────────

interface InstructionMoveProps {
  readonly slide: InstructionSlide;
  readonly index: number;
}

function InstructionMove({ slide, index }: Readonly<InstructionMoveProps>) {
  const { t } = useTranslation();
  const preset = getMovePreset(index);
  const accent = slide.accent ?? preset.accent;
  const tagLabel = slide.tagLabel ?? (preset.tagLabelKey ? t(preset.tagLabelKey) : '');
  const iconSrc = INSTRUCTION_ICONS[slide.icon ?? preset.icon];
  const moveLabel =
    slide.moveLabel ??
    t('instructions.moveLabel', { number: String(index + 1).padStart(2, '0') });

  return (
    <li className="move" style={{ '--move-accent': accent } as React.CSSProperties}>
      <div className="moveHeader">
        <span className="moveMarker" aria-hidden />
        <span className="moveLabel">{moveLabel}</span>
        {tagLabel ? <span className="moveTag">{tagLabel}</span> : null}
      </div>

      <div className="moveCard">
        <div className="moveCardHead">
          {iconSrc ? (
            <span className="moveIcon">
              <img src={iconSrc} alt="" aria-hidden />
            </span>
          ) : null}
          <div className="moveCardHeadText">
            <h3 className="moveCardTitle">{slide.title}</h3>
            {slide.description ? (
              <p className="moveCardDescription">{slide.description}</p>
            ) : null}
          </div>
        </div>

        {slide.items.length > 0 ? (
          <ul className="movePoints">
            {slide.items.map((item) => (
              <li className="movePoint" key={item.id}>
                <span className="movePointBullet" aria-hidden />
                {/* Points render description-only; a point with no description
                    (e.g. anti-cheat rules) falls back to its title as the text. */}
                <p className="movePointText">{item.description || item.title}</p>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

export function GameInstructionsScreen({
  stage,
  contentByStage,
  isPlayPrimaryDisabled = false,
  onPlayNow,
  onLearnHowToPlay,
  className,
}: Readonly<GameInstructionsScreenProps>) {
  const { i18n, t } = useTranslation();
  const [pendingAction, setPendingAction] = useState<'play' | 'learn' | null>(null);
  const theme = STAGE_THEME_COLORS[stage];
  const overlay = STAGE_OVERLAYS[stage];
  const content = useMemo(
    () => parseInstructionStage(i18n, stage, contentByStage),
    [contentByStage, i18n, stage],
  );
  const isPlayPending = pendingAction === 'play';
  const isLearnPending = pendingAction === 'learn';
  const areCtasDisabled = pendingAction !== null || isPlayPrimaryDisabled || !onPlayNow;

  const runAction = useCallback(
    async (action: 'play' | 'learn', handler?: (stage: StageId) => void | Promise<void>) => {
      if (!handler || pendingAction !== null) {
        return;
      }
      setPendingAction(action);
      try {
        await handler(stage);
      } catch {
        setPendingAction(null);
      }
    },
    [pendingAction, stage],
  );

  // The CTAs float over the scroll area rather than sitting in flow, so the
  // scroll area's bottom padding is measured live from their real height —
  // it has to track whether the learn button is present automatically.
  const ctaRef = useRef<HTMLDivElement | null>(null);
  const [ctaHeight, setCtaHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const measure = () => setCtaHeight(el.offsetHeight);
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className="instructionViewport">
      <section
        className={`${className ?? ''} instructionRoot`.trim()}
        style={
          {
            backgroundColor: theme.background,
            '--eclipse-color': theme.eclipse,
          } as React.CSSProperties
        }
        aria-label="Game instructions screen"
      >
        <img src={overlay} alt="" aria-hidden className="overlay" />
        <div
          className="eclipse"
          style={{ '--eclipse-color': theme.eclipse } as React.CSSProperties}
        />

        <FadeSlide
          className="content"
          style={{ '--cta-height': `${ctaHeight}px` } as React.CSSProperties}
        >
          <div className="game-header relative">
            <div className="metaRow">
              <span className="metaText">{resolveGameLabel(content.gameLabel, stage)}</span>
              <span className="statusPill">{content.statusLabel}</span>
            </div>
            <h1 className="gameTitle">{content.gameTitle}</h1>
            {/* TODO(temp): debug-only reset button — visible on every screen */}
            <DebugClearStorageButton />
          </div>

          <h2 className="screenHeading">
            {content.screenHeading ?? t('instructions.screenHeading')}
          </h2>

          <div className="movesScroll">
            <ol className="moveList">
              {content.slides.map((slide, index) => (
                <InstructionMove key={slide.id} slide={slide} index={index} />
              ))}
            </ol>
          </div>

          <div className="ctaStack" ref={ctaRef}>
            <button
              className={`button buttonPrimary ${isPlayPending ? 'buttonLoading' : areCtasDisabled ? 'buttonDisabled' : ''}`.trim()}
              type="button"
              disabled={areCtasDisabled}
              aria-busy={isPlayPending}
              onClick={() => {
                void runAction('play', onPlayNow);
              }}
            >
              {isPlayPending ? t('common.loading') : t('instructions.primaryCtaLabel')}
            </button>
            {onLearnHowToPlay ? (
              <button
                className={`button buttonSecondary ${isLearnPending ? 'buttonLoading' : pendingAction !== null ? 'buttonDisabled' : ''}`.trim()}
                type="button"
                disabled={pendingAction !== null}
                aria-busy={isLearnPending}
                onClick={() => {
                  void runAction('learn', onLearnHowToPlay);
                }}
              >
                {isLearnPending ? t('common.loading') : t('instructions.secondaryCtaLabel')}
              </button>
            ) : null}
          </div>
        </FadeSlide>
      </section>
    </div>
  );
}
